import {useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult} from '@tanstack/react-query';
import {consentsApi} from '../api';
import {ConsentDocumentDetailModel, UpdateConsentDocumentParams} from '../model';
import {useNotifications} from '../../../shared';

type UseUpdateConsentDocumentState = {
  getDocument: UseQueryResult<ConsentDocumentDetailModel, Error>;
  updateDocumentMutation: UseMutationResult<ConsentDocumentDetailModel, Error, UpdateConsentDocumentParams>;
  archiveDocumentMutation: UseMutationResult<void, Error, string>;
  refetchDocument: () => void;
};

export const useUpdateConsentDocument = (documentId: string): UseUpdateConsentDocumentState => {
  const queryClient = useQueryClient();
  const {createNotification} = useNotifications();

  const getDocument = useQuery<ConsentDocumentDetailModel, Error>({
    queryKey: ['consent-document', documentId],
    queryFn: () => consentsApi.getDocumentById(documentId),
    enabled: !!documentId,
    staleTime: 60 * 1000,
  });

  const refetchDocument = () => {
    queryClient.invalidateQueries({queryKey: ['consent-document', documentId]});
  };

  const updateDocumentMutation = useMutation<ConsentDocumentDetailModel, Error, UpdateConsentDocumentParams>({
    mutationFn: ({documentId, title, displayText, isDistinguished, isMandatory, order}) =>
      consentsApi.updateDocument({documentId, title, displayText, isDistinguished, isMandatory, order}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['consent-document', documentId]});
      queryClient.invalidateQueries({queryKey: ['consents-documents']});
      createNotification({
        title: 'Документ успешно обновлен',
        name: 'updateConsentDocument',
        theme: 'success',
      });
    },
  });

  const archiveDocumentMutation = useMutation<void, Error, string>({
    mutationFn: (documentId) => consentsApi.archiveDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['consents-documents']});
      createNotification({
        title: 'Документ успешно заархивирован',
        name: 'archiveConsentDocument',
        theme: 'success',
      });
    },
  });

  return {
    getDocument,
    updateDocumentMutation,
    archiveDocumentMutation,
    refetchDocument,
  };
};
